import axios from '../../axios';
import { useQuery } from '@tanstack/react-query';
import type { TFormBase, TFormQuestionGroup, TError } from '@onesource/schemas';
import { ENTITY } from '../entity';

/**
 * Form Option
 */
export interface TFormOption {
  id: string;
  questionId: string;
  optionText: string;
  position: number;
}

/**
 * Form Question with options
 */
export interface TFormQuestion {
  id: string;
  formId: string;
  questionType:
    | 'multiple_choice'
    | 'multiple_select'
    | 'true_false'
    | 'short_answer'
    | 'ordering'
    | 'file_upload'
    | 'accept'
    | 'accept_with_signature'
    | 'dropdown';
  questionText: string;
  questionBody: string | null;
  explanation: string | null;
  position: number;
  metadata: Record<string, unknown>;
  isRequired: boolean;
  /** Question group this question belongs to, or null when ungrouped. */
  groupId: string | null;
  /** Profile field this question's answer is written to on submit, or null. */
  profileFieldKey: string | null;
  options: TFormOption[];
}

/**
 * Form with questions, options, and question groups
 */
export interface TFormWithQuestions extends TFormBase {
  questions: TFormQuestion[];
  groups: TFormQuestionGroup[];
}

export const getFormQueryKey = (id: string) => [ENTITY, 'form', id];

/**
 * @deprecated Use getFormQueryKey instead in non-hook contexts
 */
export const useGetFormQueryKey = (id: string) => getFormQueryKey(id);

/**
 * Hook to fetch a specific form by ID with its questions and options
 *
 * @param id - Form ID (UUID)
 */
export const useGetForm = (id: string) => {
  return useQuery<TFormWithQuestions, TError>({
    queryKey: useGetFormQueryKey(id),
    queryFn: async () => {
      const res = await axios.get<TFormWithQuestions>(`/community-hub/forms/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};
